"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardTitle, CardHeader, CardContent } from "@/components/ui/card"; // Added CardHeader, CardContent
import { cn } from "@/lib/utils";
import Combobox from "@/components/ui/combobox";
import { useTranslation, useLanguage } from "react-i18next";

interface BreedAndSizeSelectionProps {
  petType: "cat" | "dog";
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  selectedBreed: string;
  setSelectedBreed: (breed: string) => void;
  selectedDogSize: "small" | "large" | null;
  setSelectedDogSize: (size: "small" | "large" | null) => void;
  breedOptions: { value: string; label: string }[];
  breedError: string;
  dogSizeError: string;
  isLoading: boolean;
  breedComboboxRef: React.RefObject<HTMLDivElement>;
  dogSizeGroupRef: React.RefObject<HTMLDivElement>;
  petName: string; // ADDED
}

const BreedAndSizeSelection: React.FC<BreedAndSizeSelectionProps> = ({
  petType,
  selectedBreedType,
  selectedBreed,
  setSelectedBreed,
  selectedDogSize,
  setSelectedDogSize,
  breedOptions,
  breedError,
  dogSizeError,
  isLoading,
  breedComboboxRef,
  dogSizeGroupRef,
  petName, // ADDED
}) => {
  const { t, i18n } = useTranslation();

  return (
    <>
      {/* Breed Selection (conditionally rendered for purebred and crossbreed) */}
      {(selectedBreedType === "purebred" || selectedBreedType === "crossbreed") && (
        <div className="space-y-2 field-row" ref={breedComboboxRef}>
          <Label htmlFor="breed">{t("petDetails.breed")}</Label>
          <Combobox
            options={breedOptions}
            value={selectedBreed}
            onValueChange={setSelectedBreed}
            placeholder={t("petDetails.selectBreedPlaceholder")}
            disabled={isLoading}
            className={cn(breedError && "border-destructive focus-visible:ring-destructive")}
          />
          {breedError && <p className="text-sm text-destructive">{breedError}</p>}
        </div>
      )}

      {/* Dog Size Selection (conditionally rendered for mixed dogs) */}
      {petType === "dog" && selectedBreedType === "mixed" && (
        <div className="space-y-2 field-row" ref={dogSizeGroupRef}>
          <Label htmlFor="dog-size">
            { petName ? t( "petDetails.dogSize", { petName: petName }) : t( "petDetails.dogSizeUndefined") }
          </Label>
          <div className="flex gap-2">
            <Button
              variant={selectedDogSize === "large" ? "default" : "outline"}
              onClick={() => setSelectedDogSize("large")}
              className={cn(
                "flex-1",
                selectedDogSize === "large" && "bg-primary text-primary-foreground"
              )}
              disabled={isLoading}
            >
              {t("common.yes")}
            </Button>
            <Button
              variant={selectedDogSize === "small" ? "default" : "outline"}
              onClick={() => setSelectedDogSize("small")}
              className={cn(
                "flex-1",
                selectedDogSize === "small" && "bg-primary text-primary-foreground"
              )}
              disabled={isLoading}
            >
              {t("common.no")}
            </Button>
          </div>
          {dogSizeError && <p className="text-sm text-destructive">{dogSizeError}</p>}
        </div>
      )}

      {/* Breed Type Explanation Card */}
      <Card className="bg-secondary/10 dark:bg-secondary/20 border-secondary/30 dark:border-secondary/40 shadow-none">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-lg font-semibold">{t("petDetails.breedTypeExplanationTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 text-sm text-muted-foreground">
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li><strong>{t("petDetails.breedTypePurebred")}</strong> - {t("petDetails.breedTypeExplanationPurebred")}</li>
            <li><strong>{t("petDetails.breedTypeCrossbreed")}</strong> - {t("petDetails.breedTypeExplanationCrossbreed")}</li>
            <li><strong>{t("petDetails.breedTypeMixed")}</strong> - {t("petDetails.breedTypeExplanationMixed", { petType: petType })}</li>
          </ul>
        </CardContent>
      </Card>
    </>
  );
};

export default BreedAndSizeSelection;
